// src/pages/Results.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../utils/apiClient';

// Custom Sheriff Star Component for Texas (copied from Leaderboard)
const SheriffStar = ({ size = "w-4 h-4" }) => (
  <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2L15.09 8.26L22 9L17 14.14L18.18 21L12 17.77L5.82 21L7 14.14L2 9L8.91 8.26L12 2Z"
      fill="#FFD700"
    />
  </svg>
);

function Results() {
  const { raceId } = useParams();
  const [races, setRaces] = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState(raceId || '');
  const [raceScores, setRaceScores] = useState([]);
  const [raceResult, setRaceResult] = useState(null);
  const [selectedRaceName, setSelectedRaceName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedScore, setSelectedScore] = useState(null);
  const [showScoreDetails, setShowScoreDetails] = useState(false);
  const [raceDetails, setRaceDetails] = useState(null);
  const [allRaces, setAllRaces] = useState([]);
  const [allRaceScores, setAllRaceScores] = useState({});

  // Define marquee races with their badge info (copied from Leaderboard)
  const marqueeRaces = {
    'monaco': {
      name: 'Monaco GP',
      badge: '🎲',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      title: 'Monaco Dice',
      isCustom: false
    },
    'silverstone': {
      name: 'British GP', 
      badge: '⚔️',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      title: 'Silverstone Sword',
      isCustom: false
    },
    'spa': {
      name: 'Spa GP',
      badge: '🌲', 
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      title: 'Spa Forest',
      isCustom: false
    },
    'monza': {
      name: 'Monza GP',
      badge: '🏛️',
      color: 'text-green-600', 
      bgColor: 'bg-green-100',
      title: 'Monza Temple of Speed',
      isCustom: false
    },
    'cota': {
      name: 'COTA GP',
      badge: <SheriffStar size="w-4 h-4" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-800',
      title: 'Texas Sheriff',
      isCustom: true
    },
    'abu-dhabi': {
      name: 'Abu Dhabi GP',
      badge: '🐪',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100', 
      title: 'Abu Dhabi Desert Camel',
      isCustom: false
    }
  };

  // Helper function to check if predictions are open for a race (FIXED VERSION)
  const isPredictionsOpen = useCallback((race) => {
    if (!race) return false;
    
    const now = new Date();
    const raceDate = new Date(race.date);
    
    // If race date is in the past, predictions are closed
    if (raceDate < now) return false;
    
    // If qualifying has already happened, predictions are closed
    if (race.qualifyingDateTime) {
      const qualifyingTime = new Date(race.qualifyingDateTime);
      // FIX: Check prediction closing time (2 minutes before qualifying)
      const predictionClosingTime = new Date(qualifyingTime.getTime() - (2 * 60 * 1000));
      if (predictionClosingTime < now) return false;
    }
    
    return true;
  }, []);

  // Helper function to check if tips are closed (predictions were open but now closed)
  const areTipsClosed = useCallback((race) => {
    if (!race) return false;
    
    // Tips are closed if predictions are not open
    // This means either the race has happened or qualifying has started
    return !isPredictionsOpen(race);
  }, [isPredictionsOpen]);

  // Check if a race is a marquee race (copied from Leaderboard)
  const getMarqueeRaceInfo = (raceName) => {
    const normalizedName = raceName.toLowerCase();
    
    if (normalizedName.includes('monaco')) return marqueeRaces.monaco;
    if (normalizedName.includes('british') || normalizedName.includes('silverstone')) return marqueeRaces.silverstone;
    if (normalizedName.includes('belgian') || normalizedName.includes('belgium')) return marqueeRaces.spa;
    if (normalizedName.includes('monza') || normalizedName.includes('italian')) return marqueeRaces.monza;
    if (normalizedName.includes('austin') || normalizedName.includes('cota') || normalizedName.includes('united states')) return marqueeRaces.cota;
    if (normalizedName.includes('abu dhabi')) return marqueeRaces['abu-dhabi'];
    
    return null;
  };

  // Check if user is the winner of current race
  const isRaceWinner = (userScore, allScores) => {
    if (!allScores || allScores.length === 0) return false;
    
    // Sort scores by score value (highest first)
    const sortedScores = [...allScores].sort((a, b) => b.score - a.score);
    
    // Check if this user has the highest score
    return sortedScores.length > 0 && sortedScores[0].score === userScore && userScore > 0;
  };

  // Get user's badges based on race wins across ALL races (FIXED VERSION)
  const getUserBadges = (userId) => {
    const badges = [];
    
    allRaces.forEach(race => {
      const marqueeInfo = getMarqueeRaceInfo(race.raceName);
      
      // Only award badges for races that are actually completed
      if (marqueeInfo && (race.status === 'completed' || new Date(race.date) < new Date())) {
        // Check if this user won this race
        if (allRaceScores[race._id]) {
          const raceResults = Object.entries(allRaceScores[race._id])
            .map(([uid, score]) => ({ userId: uid, score }))
            .sort((a, b) => b.score - a.score);
          
          // Check if this user won (highest score) and has score > 0
          if (raceResults.length > 0 && 
              raceResults[0].userId === userId && 
              raceResults[0].score > 0) {
            badges.push(marqueeInfo);
          }
        }
      }
    });
    
    return badges;
  };

  // Get the current race info for badge checking
  const getCurrentRace = () => {
    return races.find(race => race._id === selectedRaceId);
  };

  // Fetch available races
  useEffect(() => {
    const fetchRaces = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/f1data/races/all');
        
        // Store all races for badge calculation
        setAllRaces(response.data);
        
        // Filter to only show races where tips are closed (predictions are not open)
        // This includes completed races and upcoming races past the prediction deadline
        const racesWithClosedTips = response.data
          .filter(race => {
            if (!race) return false;
            
            // Use the existing areTipsClosed function to determine if tips are closed
            return areTipsClosed(race);
          })
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setRaces(racesWithClosedTips);
        
        if (raceId) {
          setSelectedRaceId(raceId);
        } else if (racesWithClosedTips.length > 0) {
          // Select most recent race with closed tips by default
          setSelectedRaceId(racesWithClosedTips[0]._id);
        }
        
        setError('');
      } catch (err) {
        console.error('Error fetching races:', err);
        setError('Failed to load races');
      } finally {
        setLoading(false);
      }
    };

    fetchRaces();
  }, [raceId, areTipsClosed]);

  // Fetch all race scores for badge calculation (FIXED VERSION)
  useEffect(() => {
    const fetchAllRaceScores = async () => {
      if (allRaces.length === 0) return;
      
      try {
        const scores = {};
        
        // For each completed race, try to get scores
        const now = new Date();
        const completedRaces = allRaces.filter(race => 
          race && (
            race.status === 'completed' || 
            (race.date && new Date(race.date) < now)
          )
        );
        
        for (const race of completedRaces) {
          try {
            scores[race._id] = {};
            
            const raceScoreResponse = await apiClient.get(`/api/scores/race/${race._id}`);
            
            if (raceScoreResponse.data && raceScoreResponse.data.scores) {
              // Map scores by user ID for quick lookup
              raceScoreResponse.data.scores.forEach(score => {
                if (score.user && score.score > 0) { // Only include users with scores > 0
                  scores[race._id][score.user] = score.score;
                }
              });
            }
          } catch (scoreError) {
            console.log(`Could not fetch scores for race ${race._id}:`, scoreError);
            // Continue without scores for this race
          }
        }
        
        setAllRaceScores(scores);
      } catch (err) {
        console.error('Error fetching all race scores:', err);
      }
    };

    fetchAllRaceScores();
  }, [allRaces]);

  // Fetch results when race selection changes
  useEffect(() => {
    if (!selectedRaceId) return;
    
    const fetchRaceData = async () => {
      try {
        setLoading(true);
        
        // Step 1: Get race details
        const raceResponse = await apiClient.get(`/api/f1data/races/${selectedRaceId}`);
        
        const selectedRace = raceResponse.data;
        setSelectedRaceName(selectedRace.raceName);
        
        // Step 2: Fetch race results with head-to-head data
        const resultsResponse = await apiClient.get(`/api/results/race/${selectedRaceId}`);
        
        console.log('F1 race results with head-to-head data:', resultsResponse.data);
        
        // The API should return the complete result including headToHead
        setRaceResult(resultsResponse.data);
        
        // Step 3: Fetch user scores (if needed)
        const scoresResponse = await apiClient.get(`/api/scores/race/${selectedRaceId}`);
        
        console.log('User scores data:', scoresResponse.data);
        setRaceScores(scoresResponse.data.scores || []);
        
      } catch (err) {
        console.error('Error fetching race data:', err);
        setError('Failed to load race results');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRaceData();
  }, [selectedRaceId]);

  // Handle race selection change
  const handleRaceChange = (e) => {
    setSelectedRaceId(e.target.value);
  };

  // Function to view score details
  const viewScoreDetails = (score) => {
    console.log("View Score Details - Score:", score);
    console.log("View Score Details - Race Result:", raceResult);
    
    if (raceResult && raceResult.headToHead) {
      console.log("Head-to-Head data:", raceResult.headToHead);
      if (raceResult.headToHead.drivers) {
        console.log("Drivers H2H:", raceResult.headToHead.drivers);
      }
      if (raceResult.headToHead.teams) {
        console.log("Teams H2H:", raceResult.headToHead.teams);
      }
    }
    
    setSelectedScore(score);
    setShowScoreDetails(true);
  };

  // Normalize driver names for display
  const normalizeDriverNameForDisplay = (driverName) => {
    if (!driverName) return 'N/A';
    
    // Convert underscores to spaces
    driverName = driverName.replace(/_/g, ' ');
    
    // Capitalize first letters
    return driverName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Normalize team names for display
  const normalizeTeamNameForDisplay = (teamName) => {
    if (!teamName) return 'N/A';
    
    // Convert underscores to spaces
    teamName = teamName.replace(/_/g, ' ');
    
    // Capitalize each word
    return teamName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get driver code from full name (extract 3-letter code if available)
  const getDriverCode = (driverName) => {
    if (!driverName || driverName === 'N/A') return '-';
    
    // If it's already a short code (3 letters or less), return as is
    if (driverName.length <= 3) return driverName.toUpperCase();
    
    // For normalized names like "Max Verstappen", create a code
    const words = driverName.split(' ');
    if (words.length >= 2) {
      // Take first 3 letters of last name
      return words[words.length - 1].substring(0, 3).toUpperCase();
    }
    
    // Fallback: first 3 characters
    return driverName.substring(0, 3).toUpperCase();
  };

  // Get team code from full name - simple and reliable
  const getTeamCode = (teamName) => {
    if (!teamName || teamName === 'Not selected' || teamName === 'N/A') return '-';
    
    // Split by spaces and take first letter of each word
    const words = teamName.split(' ');
    if (words.length >= 2) {
      // Take first 3-4 letters from multiple words
      return words.slice(0, 3).map(word => word.charAt(0)).join('').toUpperCase();
    }
    
    // For single words, take first 3-4 characters
    return teamName.substring(0, 4).toUpperCase();
  };

  // Check prediction accuracy for podium positions
  const checkPodiumAccuracy = (predicted, actual, podiumResults) => {
    if (!predicted || !podiumResults) return null;
    
    // Check if prediction matches exact position
    if (predicted === actual) return 'correct';
    
    // Check if driver is somewhere on podium (but wrong position)
    if (podiumResults.includes(predicted)) return 'partial';
    
    // Driver not on podium at all
    return 'incorrect';
  };

  // Check regular prediction accuracy (non-podium)
  const checkRegularAccuracy = (predicted, actual) => {
    if (!predicted || !actual) return null;
    return predicted === actual ? 'correct' : 'incorrect';
  };

  // Render prediction cell with appropriate icon
  const renderPredictionCell = (prediction, actual, isDriverCell, podiumResults = null) => {
    if (!prediction) {
      return <span className="text-gray-400">-</span>;
    }

    const displayCode = isDriverCell 
      ? getDriverCode(normalizeDriverNameForDisplay(prediction))
      : getTeamCode(normalizeTeamNameForDisplay(prediction));
    
    // Only show accuracy indicators if we have race results
    let accuracy = null;
    if (raceResult) {
      accuracy = isDriverCell && podiumResults 
        ? checkPodiumAccuracy(prediction, actual, podiumResults)
        : checkRegularAccuracy(prediction, actual);
    }
    
    return (
      <div className="flex items-center justify-center gap-1">
        <span className="text-xs text-gray-900" title={isDriverCell ? normalizeDriverNameForDisplay(prediction) : normalizeTeamNameForDisplay(prediction)}>
          {displayCode}
        </span>
        {accuracy && (
          accuracy === 'correct' ? (
            <span className="text-green-500 text-sm font-bold flex-shrink-0">✓</span>
          ) : accuracy === 'partial' ? (
            <span className="text-orange-500 text-sm font-bold flex-shrink-0">~</span>
          ) : (
            <span className="text-red-500 text-sm font-bold flex-shrink-0">✗</span>
          )
        )}
      </div>
    );
  };

  if (loading && !races.length) {
    return <div className="text-center py-8">Loading race data...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Race Results</h1>
      
      {/* Race Selection */}
      <div className="mb-6">
        <label className="block text-xl text-gray-700 font-medium mb-2">
          Select Race
        </label>
        <select
          value={selectedRaceId}
          onChange={handleRaceChange}
          className="w-full p-2 border rounded"
        >
          <option value="">Select a race</option>
          {races.map(race => {
            const raceDate = new Date(race.date);
            const now = new Date();
            const isCompleted = race.status === 'completed' || raceDate < now;
            
            // Since we're only showing races with closed tips, determine if it's completed or just tips closed
            let statusText = '';
            if (isCompleted) {
              statusText = ' (Completed)';
            } else {
              statusText = ' (Tips Closed)';
            }
            
            return (
              <option key={race._id} value={race._id}>
                {race.raceName} - {raceDate.toLocaleDateString()}{statusText}
              </option>
            );
          })}
        </select>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      {!selectedRaceId ? (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-6">
          Please select a race to view results.
        </div>
      ) : loading ? (
        <div className="text-center py-8">Loading results...</div>
      ) : (
        <>
          <h2 className="text-xl font-semibold mb-4">{selectedRaceName} Results</h2>

          {/* Add status indicator for the selected race */}
          {(() => {
            const currentRace = getCurrentRace();
            if (currentRace) {
              const raceDate = new Date(currentRace.date);
              const now = new Date();
              const isCompleted = currentRace.status === 'completed' || raceDate < now;
              
              if (!isCompleted) {
                return (
                  <div className="bg-orange-100 border border-orange-400 text-orange-700 px-4 py-3 rounded mb-6">
                    <strong>Tips Closed:</strong> Predictions are closed but race results are not yet available.
                  </div>
                );
              }
            }
            return null;
          })()}

          {/* User Predictions and Scores Table */}
          {raceScores.length > 0 ? (
            <div>
              <h3 className="text-lg font-medium mb-3">Player Predictions & Scores</h3>
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr className="sticky top-0 z-20">
                        <th className="sticky left-0 bg-gray-50 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[100px] z-30">
                          User
                        </th>
                        <th className="bg-gray-50 px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          Pole
                        </th>
                        <th className="bg-gray-50 px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          P1
                        </th>
                        <th className="bg-gray-50 px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          P2
                        </th>
                        <th className="bg-gray-50 px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          P3
                        </th>
                        <th className="bg-gray-50 px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          Fast
                        </th>
                        <th className="bg-gray-50 px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          Last
                        </th>
                        <th className="bg-gray-50 px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          Dr H2H
                        </th>
                        <th className="bg-gray-50 px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          Tm H2H
                        </th>
                        <th className="bg-gray-50 px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                          Score
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Results Row - shown first if available */}
                      {raceResult && (
                        <tr className="bg-blue-50 border-b-2 border-blue-200 sticky top-[52px] z-10">
                          <td className="sticky left-0 bg-blue-50 px-3 py-4 text-sm font-bold text-blue-900 border-r border-gray-200 min-w-[100px] z-30">
                            <div className="truncate">
                              Results
                            </div>
                          </td>
                          <td className="bg-blue-50 px-2 py-4 text-xs text-blue-900 text-center font-semibold" title={normalizeDriverNameForDisplay(raceResult.polePosition)}>
                            {getDriverCode(normalizeDriverNameForDisplay(raceResult.polePosition))}
                          </td>
                          <td className="bg-blue-50 px-2 py-4 text-xs text-blue-900 text-center font-semibold" title={normalizeDriverNameForDisplay(raceResult.podium?.[0])}>
                            {getDriverCode(normalizeDriverNameForDisplay(raceResult.podium?.[0]))}
                          </td>
                          <td className="bg-blue-50 px-2 py-4 text-xs text-blue-900 text-center font-semibold" title={normalizeDriverNameForDisplay(raceResult.podium?.[1])}>
                            {getDriverCode(normalizeDriverNameForDisplay(raceResult.podium?.[1]))}
                          </td>
                          <td className="bg-blue-50 px-2 py-4 text-xs text-blue-900 text-center font-semibold" title={normalizeDriverNameForDisplay(raceResult.podium?.[2])}>
                            {getDriverCode(normalizeDriverNameForDisplay(raceResult.podium?.[2]))}
                          </td>
                          <td className="bg-blue-50 px-2 py-4 text-xs text-blue-900 text-center font-semibold" title={normalizeDriverNameForDisplay(raceResult.fastestLap)}>
                            {getDriverCode(normalizeDriverNameForDisplay(raceResult.fastestLap))}
                          </td>
                          <td className="bg-blue-50 px-2 py-4 text-xs text-blue-900 text-center font-semibold" title={normalizeDriverNameForDisplay(raceResult.firstRetirement)}>
                            {getDriverCode(normalizeDriverNameForDisplay(raceResult.firstRetirement))}
                          </td>
                          <td className="bg-blue-50 px-2 py-4 text-xs text-blue-900 text-center font-semibold" title={normalizeDriverNameForDisplay(raceResult.headToHead?.drivers?.winner)}>
                            {getDriverCode(normalizeDriverNameForDisplay(raceResult.headToHead?.drivers?.winner))}
                          </td>
                          <td className="bg-blue-50 px-2 py-4 text-xs text-blue-900 text-center font-semibold" title={normalizeTeamNameForDisplay(raceResult.headToHead?.teams?.winner)}>
                            {getTeamCode(normalizeTeamNameForDisplay(raceResult.headToHead?.teams?.winner))}
                          </td>
                          <td className="bg-blue-50 px-3 py-4 text-center text-blue-900 font-semibold">
                            -
                          </td>
                        </tr>
                      )}

                      {raceScores
                        .sort((a, b) => b.score - a.score)
                        .map((score, index) => {
                          const prediction = score.prediction?.predictions;
                          const userBadges = getUserBadges(score.user);
                          
                          return (
                            <tr key={score.username || index} className="hover:bg-gray-50">
                              <td className="sticky left-0 bg-white hover:bg-gray-50 px-3 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 min-w-[100px]">
                                <div className="flex items-center gap-2">
                                  <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded font-bold min-w-[20px] text-center">
                                    {index + 1}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <div className="truncate" title={score.username}>
                                      {score.username}
                                    </div>
                                    {/* Display ALL earned badges */}
                                    <div className="flex space-x-1">
                                      {userBadges.map((badge, badgeIndex) => (
                                        <div
                                          key={badgeIndex}
                                          className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.bgColor} ${badge.color}`}
                                          title={`${badge.title} - ${badge.name} Winner`}
                                        >
                                          {badge.isCustom ? (
                                            <span className="flex items-center">{badge.badge}</span>
                                          ) : (
                                            <span className="text-sm">{badge.badge}</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-2 py-4 text-center">
                                {renderPredictionCell(
                                  prediction?.polePosition,
                                  raceResult?.polePosition,
                                  true
                                )}
                              </td>
                              <td className="px-2 py-4 text-center">
                                {renderPredictionCell(
                                  prediction?.podium?.[0],
                                  raceResult?.podium?.[0],
                                  true,
                                  raceResult?.podium
                                )}
                              </td>
                              <td className="px-2 py-4 text-center">
                                {renderPredictionCell(
                                  prediction?.podium?.[1],
                                  raceResult?.podium?.[1],
                                  true,
                                  raceResult?.podium
                                )}
                              </td>
                              <td className="px-2 py-4 text-center">
                                {renderPredictionCell(
                                  prediction?.podium?.[2],
                                  raceResult?.podium?.[2],
                                  true,
                                  raceResult?.podium
                                )}
                              </td>
                              <td className="px-2 py-4 text-center">
                                {renderPredictionCell(
                                  prediction?.fastestLap,
                                  raceResult?.fastestLap,
                                  true
                                )}
                              </td>
                              <td className="px-2 py-4 text-center">
                                {renderPredictionCell(
                                  prediction?.firstRetirement,
                                  raceResult?.firstRetirement,
                                  true
                                )}
                              </td>
                              <td className="px-2 py-4 text-center">
                                {renderPredictionCell(
                                  prediction?.driverHeadToHead?.winner,
                                  raceResult?.headToHead?.drivers?.winner,
                                  true
                                )}
                              </td>
                              <td className="px-2 py-4 text-center">
                                {renderPredictionCell(
                                  prediction?.teamHeadToHead?.winner,
                                  raceResult?.headToHead?.teams?.winner,
                                  false
                                )}
                              </td>
                              <td className="px-3 py-4 text-center">
                                <div className="flex flex-col items-center gap-1">
                                  <span className="text-lg font-bold text-gray-900">
                                    {score.score}
                                  </span>
                                  <button 
                                    onClick={() => viewScoreDetails(score)}
                                    className="text-blue-600 hover:text-blue-800 text-xs underline"
                                  >
                                    Details
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
              {(() => {
                const currentRace = getCurrentRace();
                if (currentRace) {
                  const raceDate = new Date(currentRace.date);
                  const now = new Date();
                  const isCompleted = currentRace.status === 'completed' || raceDate < now;
                  
                  if (!isCompleted) {
                    return "No predictions submitted for this race. Results will be available after the race is completed.";
                  }
                }
                return "No scores available for this race yet.";
              })()}
            </div>
          )}

          {/* Race Results - only show if results are available */}
          {raceResult && (
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-3">Official Race Results</h3>
              <div className="bg-white shadow rounded-lg p-6">
                {/* Podium */}
                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 mb-2">Podium</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="bg-yellow-100 text-black py-2 px-4 rounded-lg mb-2">P1</div>
                      <div className="font-medium text-black">{normalizeDriverNameForDisplay(raceResult.podium[0])}</div>
                    </div>
                    <div className="text-center">
                      <div className="bg-gray-200 text-black py-2 px-4 rounded-lg mb-2">P2</div>
                      <div className="font-medium text-black">{normalizeDriverNameForDisplay(raceResult.podium[1])}</div>
                    </div>
                    <div className="text-center">
                      <div className="bg-amber-600 text-white py-2 px-4 rounded-lg mb-2">P3</div>
                      <div className="font-medium text-black">{normalizeDriverNameForDisplay(raceResult.podium[2])}</div>
                    </div>
                  </div>
                </div>
                
                {/* Other Results */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Pole Position</h4>
                    <div className="p-2 bg-gray-50 text-black rounded">
                      {raceResult.polePosition ? 
                        normalizeDriverNameForDisplay(raceResult.polePosition) : 
                        'No data available'}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Fastest Lap</h4>
                    <div className="p-2 bg-gray-50 text-black rounded">
                      {raceResult.fastestLap ? 
                        normalizeDriverNameForDisplay(raceResult.fastestLap) : 
                        'No data available'}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">First Retirement</h4>
                    <div className="p-2 bg-gray-50 text-black rounded">
                      {raceResult.firstRetirement ? 
                        normalizeDriverNameForDisplay(raceResult.firstRetirement) : 
                        'No data available'}
                    </div>
                  </div>
                  
                  {/* Driver Head-to-Head */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Driver Head-to-Head</h4>
                    <div className="p-2 bg-gray-50 rounded border">
                      {raceResult && raceResult.headToHead && raceResult.headToHead.drivers ? (
                        <div className="mb-3">
                          <p className="text-sm text-gray-500 mb-1">Prediction options:</p>
                          <div className="flex justify-around items-center text-black">
                            <span>{raceResult.headToHead.drivers.driver1?.code || 'N/A'}</span>
                            <span className="mx-2 text-xs bg-blue-100 px-3 py-1 rounded"> vs </span>
                            <span>{raceResult.headToHead.drivers.driver2?.code || 'N/A'}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 mb-3">No prediction options available</p>
                      )}
                      
                      <div className="border-t pt-2">
                        <p className="text-sm text-gray-500 mb-1">Result:</p>
                        {raceResult && raceResult.headToHead && raceResult.headToHead.drivers && raceResult.headToHead.drivers.winner ? (
                          <p className="text-gray-800"><strong>Winner:</strong> {normalizeDriverNameForDisplay(raceResult.headToHead.drivers.winner)}</p>
                        ) : (
                          <p className="text-gray-800"><strong>Winner:</strong> Not determined yet</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Team Head-to-Head */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Team Head-to-Head</h4>
                    <div className="p-2 bg-gray-50 rounded border">
                      {raceResult && raceResult.headToHead && raceResult.headToHead.teams ? (
                        <div className="mb-3">
                          <p className="text-sm text-gray-500 mb-1">Prediction options:</p>
                          <div className="flex justify-around items-center text-black">
                            <span>{normalizeTeamNameForDisplay(raceResult.headToHead.teams.team1?.name) || 'N/A'}</span>
                            <span className="mx-2 text-xs bg-blue-100 px-3 py-1 rounded"> vs </span>
                            <span>{normalizeTeamNameForDisplay(raceResult.headToHead.teams.team2?.name) || 'N/A'}</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 mb-3">No prediction options available</p>
                      )}
                      
                      <div className="border-t pt-2">
                        <p className="text-sm text-gray-500 mb-1">Result:</p>
                        {raceResult && raceResult.headToHead && raceResult.headToHead.teams && raceResult.headToHead.teams.winner ? (
                          <p className="text-gray-800"><strong>Winner:</strong> {normalizeTeamNameForDisplay(raceResult.headToHead.teams.winner)}</p>
                        ) : (
                          <p className="text-gray-800"><strong>Winner:</strong> Not determined</p>
                        )}
                      </div>
                    </div>
                  </div>                                               
                </div>
              </div>
            </div>
          )}
          
          {/* Show message if no results available for upcoming race with closed tips */}
          {!raceResult && (() => {
            const currentRace = getCurrentRace();
            if (currentRace) {
              const raceDate = new Date(currentRace.date);
              const now = new Date();
              const isCompleted = currentRace.status === 'completed' || raceDate < now;
              
              if (!isCompleted) {
                return (
                  <div className="bg-gray-100 border border-gray-400 text-gray-700 px-4 py-3 rounded mb-6">
                    <strong>Race Pending:</strong> Official results will be available after the race is completed.
                  </div>
                );
              }
            }
            return null;
          })()}

          {/* Score Details Modal/Panel */}
          {showScoreDetails && selectedScore && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-screen overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl text-black font-semibold">{selectedScore.username}'s Score Breakdown</h2>
                    <button 
                      onClick={() => setShowScoreDetails(false)} 
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <span className="text-2xl">&times;</span>
                    </button>
                  </div>
                  
                  {/* Score breakdown content */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center bg-blue-50 p-3 rounded-lg">
                      <span className="font-medium text-black">Total Score:</span>
                      <span className="text-xl font-bold text-blue-600">{selectedScore.score} points</span>
                    </div>
                    
                    {/* Detailed score breakdown */}
                    {selectedScore.prediction && raceResult && (
                      <div className="space-y-6">
                        {/* Podium Section */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-semibold mb-4 text-black">Podium Predictions</h3>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prediction</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actual Result</th>
                                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Points</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {['P1', 'P2', 'P3'].map((position, index) => {
                                  const prediction = selectedScore.prediction.predictions?.podium?.[index];
                                  const actual = raceResult.podium?.[index];
                                  
                                  // Calculate points for this position
                                  let points = 0;
                                  if (prediction && actual) {
                                    if (prediction === actual) {
                                      // Exact position match
                                      points = position === 'P1' ? 6 : position === 'P2' ? 4 : 2;
                                    } else if (raceResult.podium?.includes(prediction)) {
                                      // Right driver, wrong position
                                      points = 1;
                                    }
                                  }
                                  
                                  return (
                                    <tr key={position}>
                                      <td className="px-4 py-2 text-sm font-medium text-black">{position}</td>
                                      <td className="px-4 py-2 text-sm text-black">
                                        {prediction ? normalizeDriverNameForDisplay(prediction) : 'N/A'}
                                        {prediction && actual && prediction === actual && (
                                          <span className="ml-2 text-green-500 font-bold">✓</span>
                                        )}
                                        {prediction && actual && prediction !== actual && raceResult.podium?.includes(prediction) && (
                                          <span className="ml-2 text-orange-500 font-bold">~</span>
                                        )}
                                        {prediction && actual && !raceResult.podium?.includes(prediction) && (
                                          <span className="ml-2 text-red-500 font-bold">✗</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2 text-sm text-black">
                                        {actual ? normalizeDriverNameForDisplay(actual) : 'N/A'}
                                      </td>
                                      <td className="px-4 py-2 text-center text-sm font-bold text-black">{points}</td>
                                    </tr>
                                  );
                                })}
                                
                                {/* Podium Bonus Points Row */}
                                {(() => {
                                  // Calculate if user got all 3 podium drivers correct
                                  const predictedPodium = selectedScore.prediction.predictions?.podium || [];
                                  const actualPodium = raceResult.podium || [];
                                  
                                  // Check how many exact matches
                                  let exactMatches = 0;
                                  predictedPodium.forEach((driver, index) => {
                                    if (driver === actualPodium[index]) {
                                      exactMatches++;
                                    }
                                  });
                                  
                                  // Check if all predicted drivers are in actual podium
                                  const allPredictedInActual = predictedPodium.length === 3 && 
                                    predictedPodium.every(driver => actualPodium.includes(driver));
                                  
                                  let bonusPoints = 0;
                                  let bonusDescription = '';
                                  
                                  if (allPredictedInActual) {
                                    if (exactMatches === 3) {
                                      bonusPoints = 6;
                                      bonusDescription = '🎯 BONUS: All 3 drivers in correct order';
                                    } else {
                                      bonusPoints = 2;
                                      bonusDescription = '🎯 BONUS: All 3 drivers but wrong order';
                                    }
                                  }
                                  
                                  if (bonusPoints > 0) {
                                    return (
                                      <tr className="bg-yellow-50 border-t-2 border-yellow-200">
                                        <td className="px-4 py-2 text-sm font-bold text-yellow-800" colSpan="3">
                                          {bonusDescription}
                                        </td>
                                        <td className="px-4 py-2 text-center text-sm font-bold text-yellow-600">
                                          +{bonusPoints}
                                        </td>
                                      </tr>
                                    );
                                  }
                                  return null;
                                })()}
                                
                                <tr className="bg-blue-50">
                                  <td className="px-4 py-2 text-sm font-bold text-black" colSpan="3">Podium Points:</td>
                                  <td className="px-4 py-2 text-center text-sm font-bold text-blue-600">
                                    {(() => {
                                      let total = 0;
                                      
                                      // Individual position points
                                      ['P1', 'P2', 'P3'].forEach((position, index) => {
                                        const prediction = selectedScore.prediction.predictions?.podium?.[index];
                                        const actual = raceResult.podium?.[index];
                                        if (prediction && actual) {
                                          if (prediction === actual) {
                                            total += position === 'P1' ? 6 : position === 'P2' ? 4 : 2;
                                          } else if (raceResult.podium?.includes(prediction)) {
                                            total += 1;
                                          }
                                        }
                                      });
                                      
                                      // Add bonus points
                                      const predictedPodium = selectedScore.prediction.predictions?.podium || [];
                                      const actualPodium = raceResult.podium || [];
                                      
                                      let exactMatches = 0;
                                      predictedPodium.forEach((driver, index) => {
                                        if (driver === actualPodium[index]) {
                                          exactMatches++;
                                        }
                                      });
                                      
                                      const allPredictedInActual = predictedPodium.length === 3 && 
                                        predictedPodium.every(driver => actualPodium.includes(driver));
                                      
                                      if (allPredictedInActual) {
                                        if (exactMatches === 3) {
                                          total += 6; // All correct order bonus
                                        } else {
                                          total += 2; // All correct but wrong order bonus
                                        }
                                      }
                                      
                                      return total;
                                    })()}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Other Categories Section */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-semibold mb-4 text-black">Other Predictions</h3>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Prediction</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actual Result</th>
                                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Points</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {/* Pole Position */}
                                <tr>
                                  <td className="px-4 py-2 text-sm font-medium text-black">Pole Position</td>
                                  <td className="px-4 py-2 text-sm text-black">
                                    {selectedScore.prediction.predictions?.polePosition ? 
                                      normalizeDriverNameForDisplay(selectedScore.prediction.predictions.polePosition) : 'N/A'}
                                    {selectedScore.prediction.predictions?.polePosition === raceResult.polePosition && (
                                      <span className="ml-2 text-green-500 font-bold">✓</span>
                                    )}
                                    {selectedScore.prediction.predictions?.polePosition && 
                                     selectedScore.prediction.predictions.polePosition !== raceResult.polePosition && (
                                      <span className="ml-2 text-red-500 font-bold">✗</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-black">
                                    {raceResult.polePosition ? normalizeDriverNameForDisplay(raceResult.polePosition) : 'N/A'}
                                  </td>
                                  <td className="px-4 py-2 text-center text-sm font-bold text-black">
                                    {selectedScore.prediction.predictions?.polePosition === raceResult.polePosition ? 2 : 0}
                                  </td>
                                </tr>

                                {/* Fastest Lap */}
                                <tr>
                                  <td className="px-4 py-2 text-sm font-medium text-black">Fastest Lap</td>
                                  <td className="px-4 py-2 text-sm text-black">
                                    {selectedScore.prediction.predictions?.fastestLap ? 
                                      normalizeDriverNameForDisplay(selectedScore.prediction.predictions.fastestLap) : 'N/A'}
                                    {selectedScore.prediction.predictions?.fastestLap === raceResult.fastestLap && (
                                      <span className="ml-2 text-green-500 font-bold">✓</span>
                                    )}
                                    {selectedScore.prediction.predictions?.fastestLap && 
                                     selectedScore.prediction.predictions.fastestLap !== raceResult.fastestLap && (
                                      <span className="ml-2 text-red-500 font-bold">✗</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-black">
                                    {raceResult.fastestLap ? normalizeDriverNameForDisplay(raceResult.fastestLap) : 'N/A'}
                                  </td>
                                  <td className="px-4 py-2 text-center text-sm font-bold text-black">
                                    {selectedScore.prediction.predictions?.fastestLap === raceResult.fastestLap ? 1 : 0}
                                  </td>
                                </tr>

                                {/* First Retirement */}
                                <tr>
                                  <td className="px-4 py-2 text-sm font-medium text-black">First Retirement</td>
                                  <td className="px-4 py-2 text-sm text-black">
                                    {selectedScore.prediction.predictions?.firstRetirement ? 
                                      normalizeDriverNameForDisplay(selectedScore.prediction.predictions.firstRetirement) : 'N/A'}
                                    {selectedScore.prediction.predictions?.firstRetirement === raceResult.firstRetirement && (
                                      <span className="ml-2 text-green-500 font-bold">✓</span>
                                    )}
                                    {selectedScore.prediction.predictions?.firstRetirement && 
                                     selectedScore.prediction.predictions.firstRetirement !== raceResult.firstRetirement && (
                                      <span className="ml-2 text-red-500 font-bold">✗</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-black">
                                    {raceResult.firstRetirement ? normalizeDriverNameForDisplay(raceResult.firstRetirement) : 'N/A'}
                                  </td>
                                  <td className="px-4 py-2 text-center text-sm font-bold text-black">
                                    {selectedScore.prediction.predictions?.firstRetirement === raceResult.firstRetirement ? 1 : 0}
                                  </td>
                                </tr>

                                {/* Driver Head-to-Head */}
                                <tr>
                                  <td className="px-4 py-2 text-sm font-medium text-black">Driver H2H</td>
                                  <td className="px-4 py-2 text-sm text-black">
                                    {selectedScore.prediction.predictions?.driverHeadToHead?.winner ? 
                                      normalizeDriverNameForDisplay(selectedScore.prediction.predictions.driverHeadToHead.winner) : 'N/A'}
                                    {selectedScore.prediction.predictions?.driverHeadToHead?.winner === raceResult.headToHead?.drivers?.winner && (
                                      <span className="ml-2 text-green-500 font-bold">✓</span>
                                    )}
                                    {selectedScore.prediction.predictions?.driverHeadToHead?.winner && 
                                     selectedScore.prediction.predictions.driverHeadToHead.winner !== raceResult.headToHead?.drivers?.winner && (
                                      <span className="ml-2 text-red-500 font-bold">✗</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-black">
                                    {raceResult.headToHead?.drivers?.winner ? normalizeDriverNameForDisplay(raceResult.headToHead.drivers.winner) : 'N/A'}
                                  </td>
                                  <td className="px-4 py-2 text-center text-sm font-bold text-black">
                                    {selectedScore.prediction.predictions?.driverHeadToHead?.winner === raceResult.headToHead?.drivers?.winner ? 1 : 0}
                                  </td>
                                </tr>

                                {/* Team Head-to-Head */}
                                <tr>
                                  <td className="px-4 py-2 text-sm font-medium text-black">Team H2H</td>
                                  <td className="px-4 py-2 text-sm text-black">
                                    {selectedScore.prediction.predictions?.teamHeadToHead?.winner ? 
                                      normalizeTeamNameForDisplay(selectedScore.prediction.predictions.teamHeadToHead.winner) : 'N/A'}
                                    {selectedScore.prediction.predictions?.teamHeadToHead?.winner === raceResult.headToHead?.teams?.winner && (
                                      <span className="ml-2 text-green-500 font-bold">✓</span>
                                    )}
                                    {selectedScore.prediction.predictions?.teamHeadToHead?.winner && 
                                     selectedScore.prediction.predictions.teamHeadToHead.winner !== raceResult.headToHead?.teams?.winner && (
                                      <span className="ml-2 text-red-500 font-bold">✗</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-black">
                                    {raceResult.headToHead?.teams?.winner ? normalizeTeamNameForDisplay(raceResult.headToHead.teams.winner) : 'N/A'}
                                  </td>
                                  <td className="px-4 py-2 text-center text-sm font-bold text-black">
                                    {selectedScore.prediction.predictions?.teamHeadToHead?.winner === raceResult.headToHead?.teams?.winner ? 1 : 0}
                                  </td>
                                </tr>

                                <tr className="bg-blue-50">
                                  <td className="px-4 py-2 text-sm font-bold text-black" colSpan="3">Other Points:</td>
                                  <td className="px-4 py-2 text-center text-sm font-bold text-blue-600">
                                    {(() => {
                                      let total = 0;
                                      // Pole Position (2 points)
                                      if (selectedScore.prediction.predictions?.polePosition === raceResult.polePosition) total += 2;
                                      // Fastest Lap (1 point)
                                      if (selectedScore.prediction.predictions?.fastestLap === raceResult.fastestLap) total += 1;
                                      // First Retirement (1 point) - updated to match backend
                                      if (selectedScore.prediction.predictions?.firstRetirement === raceResult.firstRetirement) total += 1;
                                      // Driver H2H (1 point) - updated to match backend
                                      if (selectedScore.prediction.predictions?.driverHeadToHead?.winner === raceResult.headToHead?.drivers?.winner) total += 1;
                                      // Team H2H (1 point)
                                      if (selectedScore.prediction.predictions?.teamHeadToHead?.winner === raceResult.headToHead?.teams?.winner) total += 1;
                                      return total;
                                    })()}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-6 text-right">
                    <button
                      onClick={() => setShowScoreDetails(false)}
                      className="bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Badge Legend for All Possible Badges */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Marquee Race Winner Badge Legend</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-blue-700">
              {Object.entries(marqueeRaces).map(([key, badge]) => (
                <div key={key} className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.bgColor} ${badge.color}`}>
                    {badge.isCustom ? (
                      <span className="flex items-center">{badge.badge}</span>
                    ) : (
                      <span className="text-sm">{badge.badge}</span>
                    )}
                  </span>
                  <span>{badge.title}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-blue-600">
              <p>Badges are earned by winning marquee races and displayed on all results pages</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Results;